import { useState, useRef, useEffect } from "react";

const PAGE_TITLES = {
  dashboard:  { title: "Dashboard",       sub: "Your edge at a glance" },
  bestodds:   { title: "Best Odds",       sub: "Compare odds across all BW bookmakers" },
  valuebets:  { title: "Value Bets",      sub: "Positive EV opportunities right now" },
  arbitrage:  { title: "Arbitrage",       sub: "Guaranteed profit opportunities" },
  clv:        { title: "CLV Tracker",     sub: "Are you beating the closing line?" },
  bankroll:   { title: "Bankroll Manager",sub: "Kelly criterion & stake sizing" },
  middles: { title: "Middles", sub: "Bet both sides — win when the score falls in the window" },
  sharpMoney: { title: "Sharp Money", sub: "Track significant odds movements — follow the smart bettors" },
  tipsters:   { title: "Tipster Hub",     sub: "Verified leaderboard — no self-reporting" },
  telegram:   { title: "Telegram Bot",    sub: "@OddsEdgeBW_bot" },
  bookmakers: { title: "BW Bookmakers",   sub: "All 6 licensed Botswana bookmakers" },
  calculators: { title: "Calculators", sub: "Professional betting tools" },
  education: { title: "Education", sub: "Bet smarter — learn the fundamentals" },
  smartParlay: { title: "Smart Parlay", sub: "AI-powered parlay builder with live estimates" },
  heatmap: { title: "Heat Map", sub: "Where the value is concentrated today" },
  profile: { title: "Profile Settings", sub: "Manage your account" },
  billing: { title: "Billing & Plan", sub: "Manage your subscription" },
  admin: { title: "Admin Panel", sub: "Platform management" },
};

export default function Topbar({ page, user, onLogout, onToggleSidebar, onNavigate, theme, onToggleTheme }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [time, setTime] = useState(new Date());
  const menuRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const { title, sub } = PAGE_TITLES[page] || PAGE_TITLES.dashboard;
  const initials = user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "U";
  const timeStr = time.toLocaleTimeString("en-BW", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Gaborone" });

  return (
    <header style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 16px",
      borderBottom: "1px solid var(--border)",
      background: "var(--glass)",
      backdropFilter: "blur(12px)",
      position: "sticky", top: 0, zIndex: 100,
      minHeight: 56,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
        {/* Hamburger only — no logo text in topbar */}
        <button onClick={onToggleSidebar} style={{ color: "var(--text3)", padding: 4, flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</h2>
          <p style={{ fontSize: 10, color: "var(--text3)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</p>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {/* Clock — hide on very small screens */}
        <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", letterSpacing: 1, display: window.innerWidth < 380 ? "none" : "block" }}>
          {timeStr} <span style={{ fontSize: 10, opacity: .6 }}>CAT</span>
        </div>

        {/* Live badge */}
        <div style={{
          display: "flex", alignItems: "center", gap: 5,
          padding: "4px 10px",
          background: "rgba(240,71,71,0.08)",
          border: "1px solid rgba(240,71,71,0.2)",
          borderRadius: 20,
          fontSize: 10, fontWeight: 600, color: "var(--danger)",
          fontFamily: "var(--font-display)",
          flexShrink: 0,
        }}>
          <div className="live-dot" />
          LIVE
        </div>

        {/* User menu */}
        <div style={{ position: "relative" }} ref={menuRef}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            style={{
              width: 32, height: 32,
              background: "var(--accent)",
              borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontWeight: 700, fontSize: 12,
              color: "#080d17",
              border: "2px solid transparent",
              flexShrink: 0,
            }}
          >
            {initials}
          </button>
          {menuOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0,
              background: "var(--bg2)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              minWidth: 180,
              boxShadow: "var(--shadow-lg)",
              overflow: "hidden",
              zIndex: 300,
            }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{user?.name}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{user?.email}</div>
                <div style={{ marginTop: 6 }}>
                  <span className={`pill pill-${user?.plan === "pro" ? "teal" : user?.plan === "elite" ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
                    {(user?.plan || "free").toUpperCase()}
                  </span>
                </div>
              </div>
              <MenuItem label="Profile Settings" icon="⚙️" onClick={() => { setMenuOpen(false); onNavigate('profile'); }} />
              <MenuItem label="Billing & Plan" icon="💳" onClick={() => { setMenuOpen(false); onNavigate('billing'); }} />
              <MenuItem 
  label={theme === 'dark' ? 'Light Mode' : 'Dark Mode'} 
  icon={theme === 'dark' ? '☀️' : '🌙'}
  onClick={() => { onToggleTheme(); setMenuOpen(false); }} 
/>
              <div style={{ borderTop: "1px solid var(--border)" }}>
                <button
                  onClick={onLogout}
                  style={{
                    width: "100%", padding: "10px 16px",
                    display: "flex", alignItems: "center", gap: 8,
                    fontSize: 13, color: "var(--danger)",
                    background: "transparent", cursor: "pointer",
                    transition: "background .12s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(240,71,71,0.08)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span>🚪</span> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MenuItem({ label, icon, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", padding: "10px 16px",
        display: "flex", alignItems: "center", gap: 8,
        fontSize: 13, color: "var(--text2)",
        background: "transparent", cursor: "pointer",
        transition: "background .12s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--bg3)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <span>{icon}</span> {label}
    </button>
  );
}
